<template>
  <BaseSideModal :width="'820px'" @close="$emit('close')">
    <template #title>
      <span>{{ $t(`carriers.import.snapshot.title.${kind}`) }}</span>
    </template>

    <!-- See AllUpdatesDrawer: a second line in the title slot overflows the
         modal, because its content height assumes a one-line header. -->
    <p class="snapshot-review__subtitle">{{ subtitle }}</p>

    <!-- What the last batch did, and it stays: a toast is gone by the time you
         look up from the row you were reading. -->
    <p v-if="notice" class="snapshot-review__notice">{{ notice }}</p>

    <!-- One tab per answer. What still needs a decision is what the person came
         for, so it is where the review opens; what was added and what was left
         out are there to look back at, not to scroll past. -->
    <div v-if="!readOnly" class="snapshot-review__tabs">
      <BaseTabs>
        <BaseTabsTab
          v-for="t in tabs"
          :key="t.key"
          :active="tab === t.key"
          :badge-text="formatCount(t.count)"
          @click="switchTab(t.key)"
        >
          {{ $t(`carriers.import.snapshot.tab.${t.key}`) }}
        </BaseTabsTab>
      </BaseTabs>
    </div>

    <BasePreloader v-if="loading && !rows.length" color="purple" size="l"/>

    <p v-else-if="!rows.length" class="snapshot-review__empty">
      {{ readOnly
        ? $t('carriers.import.snapshot.Empty')
        : $t(`carriers.import.snapshot.emptyTab.${tab}`) }}
    </p>

    <template v-else>
      <div v-if="!readOnly && tab === 'WAITING'" class="snapshot-review__bar">
        <el-checkbox
          :value="allSelected"
          :indeterminate="someSelected"
          @change="toggleAll"
        >{{ $t('carriers.import.snapshot.SelectAll') }}</el-checkbox>
        <span class="snapshot-review__count">
          {{ $t('carriers.import.snapshot.Selected', {
            count: selected.length, total: pendingTotal,
          }) }}
        </span>
      </div>

      <div
        v-for="row in rows"
        :key="row.id"
        class="snapshot-review__row"
        :class="{ 'snapshot-review__row--failed': failures[row.id] }"
      >
        <el-checkbox
          v-if="row.reviewStatus === 'PENDING' && !readOnly"
          :value="selected.includes(row.id)"
          @change="toggle(row.id)"
        />
        <!-- Keeps a resolved or unselectable row aligned with the rest. -->
        <span v-else class="snapshot-review__spacer"></span>

        <div class="snapshot-review__main">
          <p class="snapshot-review__policy">
            {{ row.policyNumber }}
            <!-- Across an account's reads, which read staged the row: the only hint of how
                 old the carrier's data is. -->
            <span v-if="account && row.readAt" class="snapshot-review__badge">
              {{ $t('carriers.import.snapshot.ReadAt', { when: formatWhen(row.readAt, $i18n.locale) }) }}
            </span>
            <!-- A colectivo is held by a company, not a person. -->
            <span v-if="row.companyName" class="snapshot-review__badge">
              {{ $t('carriers.import.snapshot.GroupPolicy', { company: row.companyName }) }}
            </span>
            <!-- Who the carrier lists under a colectivo; each becomes its own policy on
                 apply. The only people hint left, since the members diff rows are hidden.
                 Family dependents are no longer saved, so they get no badge. -->
            <span v-if="row.memberCount" class="snapshot-review__badge">
              {{ $t('carriers.import.snapshot.MemberCount', { n: formatCount(row.memberCount) }) }}
            </span>
            <!-- The policy's own document as the read stored it, in its own tab, to check
                 before applying. Only where the carrier gave one. -->
            <a
              v-if="row.caratulaUrl"
              class="snapshot-review__doc"
              :href="row.caratulaUrl"
              target="_blank"
              rel="noopener"
            >{{ $t('carriers.import.snapshot.Caratula') }}</a>
          </p>
          <!-- The client the read found or created for this row, opened in its own tab so the
               review stays where it was. A row with no profile has nobody to open. -->
          <p class="snapshot-review__client">
            <a
              v-if="row.personProfileId"
              class="snapshot-review__client-link"
              :href="profileHref(row.personProfileId)"
              target="_blank"
              rel="noopener"
            >{{ row.clientName }}</a>
            <template v-else>{{ row.clientName }}</template>
          </p>
          <!-- Why the daily sync left it for a person rather than applying it itself --
               only the reasons the row does not already show. -->
          <p v-if="visibleHoldReasons(row).length" class="snapshot-review__held">
            <span
              v-for="reason in visibleHoldReasons(row)"
              :key="reason"
              class="snapshot-review__held-chip"
            >{{ $t(`carriers.import.daily.hold.${reason}`) }}</span>
          </p>

          <!-- Only what would move. The backend sends every field with a
               `changed` flag so the big modal can show what the carrier agrees
               with us about; in a list of fifty rows that is noise, and the
               answer here is "what is this one asking me to accept". -->
          <table v-if="movedIn(row).length" class="snapshot-review__changes">
            <tr>
              <th>{{ $t('carriers.import.snapshot.Field') }}</th>
              <th>{{ $t('carriers.import.snapshot.InRenova') }}</th>
              <th>{{ $t('carriers.import.snapshot.AtTheCarrier') }}</th>
            </tr>
            <tr v-for="change in movedIn(row)" :key="change.field">
              <td>{{ fieldLabel(change.field) }}</td>
              <td class="snapshot-review__old">{{ format(change, 'current') }}</td>
              <td class="snapshot-review__new">{{ format(change, 'incoming') }}</td>
            </tr>
          </table>
          <!-- Not when what moved is only hidden people rows: "the same values" would
               then be untrue. -->
          <p v-else-if="kind === 'UPDATE' && !hiddenMoved(row)" class="snapshot-review__same">
            {{ $t('carriers.import.snapshot.NothingMoved') }}
          </p>

          <p v-if="row.skipReason && !row.clientAssigned" class="snapshot-review__why">
            {{ skipText(row.skipReason) }}
            <span v-if="row.skipDetail">— {{ row.skipDetail }}</span>
          </p>
          <p v-if="row.clientAssigned" class="snapshot-review__why">
            {{ $t('carriers.import.assign.Assigned') }}
          </p>
          <!-- The carrier gave nothing to recognise the client by, so a person
               names them; the row then waits with the new policies. -->
          <BaseButton
            v-if="row.skipReason === 'NO_CLIENT_KEY' && row.reviewStatus === 'PENDING' && canApply"
            type="outline"
            is-small
            class="snapshot-review__assign"
            @click="assigning = row"
          >{{ $t('carriers.import.assign.Button') }}</BaseButton>

          <!-- Left where it is, with the reason under it, rather than vanishing
               into a toast that says only how many. -->
          <p v-if="failures[row.id]" class="snapshot-review__failure">
            {{ failures[row.id] }}
          </p>
        </div>

        <!-- What became of the row, then its action: on the right where every
             other row action in this app sits, and in the kit's own outline
             style rather than a bare <button> that inherits nothing. -->
        <div v-if="!readOnly" class="snapshot-review__side">
          <a
            v-if="row.reviewStatus === 'APPLIED' && row.appliedPolicyId"
            class="snapshot-review__outcome snapshot-review__outcome--done"
            :href="policyHref(row.appliedPolicyId)"
            target="_blank"
            rel="noopener"
            :title="$t('carriers.import.snapshot.OpenPolicy')"
          >✓ {{ outcomeText(row) }} ↗</a>
          <span
            v-else-if="row.reviewStatus !== 'PENDING'"
            class="snapshot-review__outcome"
            :class="`snapshot-review__outcome--${outcomeTone(row)}`"
          >{{ outcomeText(row) }}</span>
          <BaseButton
            type="outline"
            is-small
            class="snapshot-review__look"
            @click="look(row)"
          >{{ kind === 'NEW'
            ? $t('carriers.import.preview.Look')
            : $t('carriers.import.diff.Look') }}</BaseButton>
        </div>
      </div>

      <!-- The next page arrives when this scrolls into view. A button here made
           a group of four hundred a matter of pressing it eight times. -->
      <div v-if="hasMore" ref="more" class="snapshot-review__more">
        <BasePreloader v-if="loading" color="purple" size="s"/>
      </div>
    </template>

    <template #footer>
      <!-- Only while there is more than one request in flight: "applying 25 of
           25" tells nobody anything. -->
      <div v-if="progress" class="snapshot-review__progress">
        <p class="snapshot-review__progress-text">
          {{ $t('carriers.import.snapshot.Applying', {
            done: progress.done, total: progress.total,
          }) }}
        </p>
        <div class="snapshot-review__progress-track">
          <div
            class="snapshot-review__progress-fill"
            :style="{ width: progressWidth }"
          ></div>
        </div>
      </div>
      <p v-else class="snapshot-review__note">{{ $t('carriers.import.snapshot.Note') }}</p>
      <!-- A list to read is also a list to hand on: the carrier's own record for every row, as the
           screen filters it. -->
      <BaseButton
        v-if="readOnly && rows.length"
        type="outline"
        :loading="downloading"
        @click="download"
      >{{ $t('paymentImportHistory.exportCsv') }}</BaseButton>
      <BaseButton type="outline" @click="$emit('close')">{{ $t('buttons.back') }}</BaseButton>
      <template v-if="!readOnly && canApply && tab === 'WAITING'">
        <BaseButton
          type="outline"
          :disabled="!selected.length || busy"
          :loading="busy === 'dismiss'"
          @click="resolve('dismiss')"
        >
          {{ $t('carriers.import.snapshot.Dismiss') }}
        </BaseButton>
        <BaseButton
          :disabled="!selected.length || busy"
          :loading="busy === 'apply'"
          @click="resolve('apply')"
        >
          {{ $t('carriers.import.snapshot.Apply') }}
        </BaseButton>
      </template>
    </template>
    <AssignClientDialog
      v-if="assigning"
      :row="assigning"
      @assigned="onAssigned"
      @close="assigning = null"
    />

    <!-- Keyed: another row is a fresh modal, so no tick carries over to it. -->
    <SnapshotPolicyPreviewModal
      v-if="previewing"
      :key="previewing.id"
      :row="previewing"
      :carrier-name="carrierName"
      @resolved="onPreviewResolved"
      @close="previewing = null"
    />

    <SnapshotPolicyDiffModal
      v-if="diffing !== null"
      :rows="diffRows"
      :start-index="diffing"
      :carrier-name="carrierName"
      @resolved="onDiffResolved"
      @close="diffing = null"
    />
  </BaseSideModal>
</template>

<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import BaseTabs from '@/views/RenovaDashboard/components/ui-kit/BaseTabs';
import BaseTabsTab from '@/views/RenovaDashboard/components/ui-kit/BaseTabsTab';
import { UserPermissions } from '@/utils/permissions';
import { apiError, formatCount, formatWhen } from '@/utils/carrierImport';
import { diffFieldLabel, formatDiffSide, isShownDiffField } from '@/utils/carrierFormat';
import rowSelection from './mixins/rowSelection';
import SnapshotPolicyPreviewModal from './SnapshotPolicyPreviewModal';
import SnapshotPolicyDiffModal from './SnapshotPolicyDiffModal';
import AssignClientDialog from './AssignClientDialog';

const PAGE = 50;

/**
 * The hold reasons worth a chip. The rest repeat what the row already says: the
 * NEW list's title says "not in Renova", and a changed field has its own diff
 * row right below. PEOPLE_CHANGED stays for group policies only (visibleHoldReasons).
 * Front-end only: the backend keeps every reason for filtering and analytics.
 */
const VISIBLE_HOLD_REASONS = [
  'NEW_CLIENT', 'MATCHED_BY_NAME', 'PEOPLE_CHANGED', 'SCHEDULE_WOULD_EMPTY', 'AUTO_APPLY_FAILED',
];

/**
 * The tabs, and the review states each one lists. APPLYING is a batch in
 * flight; it reads as reviewed, because somebody already said yes to it.
 */
const TABS = {
  WAITING: ['PENDING'],
  REVIEWED: ['APPLYING', 'APPLIED'],
  // SUPERSEDED: a newer read of the account staged the policy again and took the row over.
  SKIPPED: ['DISMISSED', 'SUPERSEDED'],
};

/**
 * The two read-only lists, both SKIPPED rows split by why. NOT_PRIMARY is a
 * policy the carrier lists on the client and the client's own portal page does
 * not -- as far as this screen can tell, one held with another agent. Everything
 * else is a policy the read could not use. Excluded rather than listed, so a
 * reason added on the backend later still lands somewhere.
 */
const READ_ONLY = {
  SKIPPED: { excludeSkipReasons: ['NOT_PRIMARY'] },
  OTHER_AGENTS: { skipReasons: ['NOT_PRIMARY'] },
  // Rows the portal could not key a client for: the one skip a person can resolve, by naming
  // the client on the row. Its own list, so "Name the clients" does not open every skip.
  ASSIGN: { skipReasons: ['NO_CLIENT_KEY'] },
};

/**
 * How many rows one apply request carries.
 *
 * Applying runs the real importer per row, inside the request — a profile
 * resolution, a transaction and sometimes an S3 round trip each. Four hundred
 * of those in one call is a gateway timeout and no way to tell what got
 * written. Twenty-five keeps each request ordinary and lets the screen say how
 * far it has got. Dismissing is a single UPDATE whatever the size, so it is not
 * chunked.
 */
const APPLY_CHUNK = 25;

/**
 * What a carrier read found and has not written, and the choice about it.
 *
 * A read started from Carriers stages everything and touches nothing; this is
 * where a person looks at it and decides. Four groups, opened one at a time:
 * what would be created, what would be corrected, and two lists that are not
 * decisions and so offer no buttons -- policies the client holds with other
 * agents, and policies that could not be read.
 *
 * Applying re-runs the real import path server-side, so nothing here needs to
 * know how a policy is written; it only needs to say which rows.
 */
export default {
  name: 'SnapshotReviewModal',
  components: {
    BaseSideModal,
    BaseButton,
    BasePreloader,
    BaseTabs,
    BaseTabsTab,
    SnapshotPolicyPreviewModal,
    SnapshotPolicyDiffModal,
    AssignClientDialog,
  },
  mixins: [UserPermissions, rowSelection],
  props: {
    /** One run's rows; null with `account` for every run of that account. */
    run: { type: Object, default: null },
    /**
     * { scrapeAccountId, accountLabel, carrierName, latestFinishedAt }: the cumulative review,
     * every pending row the account's reads ever staged. What "Need your input" opens.
     */
    account: { type: Object, default: null },
    /** NEW | UPDATE | SKIPPED | OTHER_AGENTS | ASSIGN — which group is being reviewed. */
    kind: { type: String, required: true },
    carrierName: { type: String, default: '' },
  },
  emits: ['close', 'resolved'],
  data() {
    return {
      rows: [],
      /** WAITING | REVIEWED | SKIPPED — which tab is showing. */
      tab: 'WAITING',
      /** Rows of the tab already fetched, and how many it has (null: not asked yet). */
      offset: 0,
      tabTotal: null,
      loading: true,
      busy: null,
      /** What the last apply or dismiss did, kept on screen. */
      notice: '',
      // Both must be declared here or Vue 2 cannot make them reactive and the
      // modals never open -- `selected` and `failures` come from rowSelection.
      /** The NEW row being previewed. */
      previewing: null,
      /** The no-client-key row whose client is being named. */
      assigning: null,
      /**
       * Rows of this group still awaiting a decision, across every page -- the
       * number "select all" aims at and the "Not reviewed" heading shows.
       */
      pendingTotal: 0,
      /** What each tab holds, for its badge. */
      tabCounts: { WAITING: 0, REVIEWED: 0, SKIPPED: 0 },
      /** { done, total } while a chunked apply is running. */
      progress: null,
      /**
       * Which load() is current. Every call takes the next number and only the latest may touch
       * the list: a tab switched away from and back, or a reload after Add while a page was still
       * arriving, otherwise let the older answer append the same rows a second time.
       */
      loadSeq: 0,
      /** A CSV download in flight. */
      downloading: false,
      /** Watches the bottom of the list, for the next page. */
      observer: null,
      /** The queue the diff modal walks -- see look(). */
      diffRows: [],
      /** Index into diffRows of the UPDATE being diffed, or null. */
      diffing: null,
    };
  },
  computed: {
    /**
     * Select-all means the whole group, not the page.
     *
     * The mixin defaults this to what is loaded, which ticked fifty rows while
     * the label beside it said "of 412" and gave the person no way to know
     * which fifty.
     */
    selectionTotal() {
      return this.pendingTotal;
    },
    progressWidth() {
      if (!this.progress || !this.progress.total) return '0%';
      return `${Math.round((this.progress.done / this.progress.total) * 100)}%`;
    },
    /** What the diff modal walks: only rows still awaiting a decision. */
    pendingRows() {
      return this.rows.filter((r) => r.reviewStatus === 'PENDING');
    },
    /** SKIPPED and OTHER_AGENTS are lists to read, not decisions to make. */
    readOnly() {
      return Boolean(READ_ONLY[this.kind]);
    },
    /** What the list asks the server for, before paging and status: one run, or one account. */
    query() {
      return {
        scrapeJobId: this.run ? this.run.scrapeJobId : undefined,
        scrapeAccountId: this.account ? this.account.scrapeAccountId : undefined,
        kinds: [this.readOnly ? 'SKIPPED' : this.kind],
        ...(READ_ONLY[this.kind] || {}),
      };
    },
    /** "AXA · 9 Oct" for a run; "AXA · agent 12345 · every read" for an account. */
    subtitle() {
      if (this.account) {
        return [
          this.account.carrierName || this.carrierName,
          this.account.accountLabel,
          this.$t('carriers.import.snapshot.EveryRead'),
        ].filter(Boolean).join(' · ');
      }
      return `${this.carrierName} · ${formatWhen(this.run.finishedAt || this.run.updatedAt, this.$i18n.locale)}`;
    },
    hasMore() {
      return this.tabTotal === null || this.offset < this.tabTotal;
    },
    tabs() {
      return Object.keys(TABS).map((key) => ({ key, count: this.tabCounts[key] }));
    },
    canApply() {
      return this.checkUserPermissions([this.PermissionsTypes.POLICIES_UPDATE]);
    },
  },
  created() {
    // The account's waiting rows are re-checked against the book first, so nothing
    // that would no longer change Renova is listed (see carrierImportRecheckSnapshots).
    const recheck = this.account && this.account.scrapeAccountId && this.canApply
      ? this.$store.dispatch('carrierImportRecheckSnapshots', this.account.scrapeAccountId)
      : Promise.resolve(null);
    recheck.then(() => {
      this.load(true);
      this.countTabs();
    });
  },
  mounted() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0] && entries[0].isIntersecting && !this.loading && this.hasMore) this.load();
    });
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    formatWhen,
    formatCount,
    /**
     * The rows that would actually move, less the ones the review hides.
     *
     * `changes` carries every field with a `changed` flag, and a staged row from
     * before the diff was audited can carry none at all — treat a row without
     * the flag as moving, rather than hiding it.
     */
    movedIn(row) {
      return ((row && row.changes) || [])
        .filter((c) => c.changed !== false && isShownDiffField(c.field));
    },
    /** Something moved, but only in rows the review hides (group members). */
    hiddenMoved(row) {
      return ((row && row.changes) || [])
        .some((c) => c.changed !== false && !isShownDiffField(c.field));
    },
    visibleHoldReasons(row) {
      // "Group members changed" only on a group policy: an older backend also
      // holds a family policy whose dependents changed, and dependents are no
      // longer saved, so for anything but a group the reason means nothing.
      const isGroup = Boolean(row && (row.memberCount || row.companyName));
      return ((row && row.holdReasons) || [])
        .filter((r) => VISIBLE_HOLD_REASONS.includes(r))
        .filter((r) => r !== 'PEOPLE_CHANGED' || isGroup);
    },
    format(change, side) {
      return formatDiffSide(change, side, this.$t.bind(this), this.$tc.bind(this));
    },
    /** The same label the big modal uses, from the same helper. */
    fieldLabel(field) {
      return diffFieldLabel(field, this.$t.bind(this), this.$te.bind(this));
    },
    /**
     * A policy that does not exist yet is previewed; one that does is diffed.
     *
     * A row still awaiting a decision opens inside the pending queue, so it can
     * be walked with next-previous. A row already applied or dismissed opens on
     * its own: it is history, there is nothing to walk to, and indexing it into
     * the pending queue is what previously landed on the wrong policy -- or, in
     * a group with nothing left pending, on no policy at all.
     */
    look(row) {
      if (this.kind === 'NEW') { this.previewing = row; return; }
      const at = this.pendingRows.findIndex((r) => r.id === row.id);
      this.diffRows = at >= 0 ? this.pendingRows : [row];
      this.diffing = at >= 0 ? at : 0;
    },
    /**
     * A client was named: the row is now a new policy waiting to be applied,
     * so it leaves this list and the run's counts move.
     */
    onAssigned() {
      this.assigning = null;
      this.notice = this.$t('carriers.import.assign.Done');
      this.$emit('resolved');
      this.load(true);
      if (!this.readOnly) this.countTabs();
    },
    /** A NEW row applied from its preview: it leaves this tab like a diffed one. */
    onPreviewResolved() {
      this.previewing = null;
      this.onDiffResolved();
    },
    onDiffResolved() {
      this.$emit('resolved');
      // The counts too: a row decided in the diff leaves its tab, and the badges and select-all
      // total would otherwise keep counting it.
      this.load(true);
      this.countTabs();
    },
    /**
     * The reason in words, or the raw reason when there are none.
     *
     * $t renders the key itself for a miss, so a reason added on the backend
     * before its copy lands here would have put
     * "carriers.import.snapshot.skip.SOMETHING" on the screen — which is what it
     * did for NOT_PRIMARY. $te asks first, so the worst case is a bare code.
     */
    skipText(reason) {
      const key = `carriers.import.snapshot.skip.${reason}`;
      return this.$te(key) ? this.$t(key) : reason;
    },
    /**
     * What became of the row: added or updated, not yet, or skipped.
     *
     * The backend enum is PENDING, APPLYING, APPLIED, DISMISSED. APPLYING is
     * real -- claimForApply sets it while a batch is in flight and this list
     * reloads between chunks. A status this screen has no words for shows as
     * itself rather than as a raw key.
     */
    outcomeText(row) {
      const status = row.reviewStatus;
      // Closed by a re-check, not by a newer read: Renova already holds what it proposed.
      if (status === 'SUPERSEDED' && !row.supersededByJobId) {
        return this.$t('carriers.import.snapshot.outcome.UP_TO_DATE');
      }
      const key = status === 'APPLIED'
        ? `carriers.import.snapshot.outcome.${this.kind}.${status}`
        : `carriers.import.snapshot.outcome.${status}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    outcomeTone(row) {
      return {
        PENDING: 'waiting', APPLYING: 'busy', APPLIED: 'done', DISMISSED: 'skipped', SUPERSEDED: 'skipped',
      }[row.reviewStatus] || 'waiting';
    },
    profileHref(id) {
      return this.$router.resolve({ name: 'userDetail', params: { id } }).href;
    },
    download() {
      this.downloading = true;
      return this.$store.dispatch('carrierImportExportSnapshot', this.query)
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${this.kind.toLowerCase()}.csv`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => this.$notify.error({
          message: apiError(err, this.$t('adminTitles.someError')),
        }))
        .finally(() => { this.downloading = false; });
    },
    policyHref(id) {
      return this.$router.resolve({ name: 'policy', params: { id } }).href;
    },
    /**
     * What each tab holds, however little of it is on screen: one row fetched
     * per tab for its `total`. WAITING is also what select-all aims at.
     */
    countTabs() {
      if (this.readOnly) return Promise.resolve();
      const count = (reviewStatuses) => this.$store.dispatch('carrierImportFetchSnapshot', {
        ...this.query, reviewStatuses, limit: 1, offset: 0,
      }).then((data) => (data && data.total) || 0);
      return Promise.all(Object.keys(TABS).map((key) => count(TABS[key])
        .then((n) => { this.tabCounts = { ...this.tabCounts, [key]: n }; })
        .catch(() => {})))
        .then(() => { this.pendingTotal = this.tabCounts.WAITING; });
    },
    switchTab(key) {
      if (key === this.tab) return;
      this.tab = key;
      this.load(true);
    },
    /** Every pending row of the group, in one request -- the group as the list queries it, run or account. */
    selectAll() {
      return this.$store.dispatch('carrierImportFetchSnapshotIds', this.query)
        .then((data) => {
          this.selected = (data && data.ids) || [];
          // The authority on the group's size: it counted the same rows it
          // returned, so the checkbox cannot end up half-ticked against a
          // total taken at a different moment.
          if (data && typeof data.total === 'number') this.pendingTotal = data.total;
          return this.selected;
        })
        .catch((err) => {
          this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) });
          return [];
        });
    },
    /** The next page of the tab that is showing. */
    load(reset = false) {
      if (reset) {
        this.rows = [];
        this.offset = 0;
        this.tabTotal = null;
      }
      this.loading = true;
      this.loadSeq += 1;
      const seq = this.loadSeq;
      const { tab } = this;
      return this.$store.dispatch('carrierImportFetchSnapshot', {
        ...this.query,
        reviewStatuses: this.readOnly ? undefined : TABS[tab],
        limit: PAGE,
        offset: this.offset,
      })
        .then((data) => {
          // A newer load() has started since -- another tab, or a reload -- and owns the list now.
          if (seq !== this.loadSeq) return;
          const items = (data && data.items) || [];
          this.rows = this.rows.concat(items);
          this.offset += items.length;
          this.tabTotal = (data && data.total) || 0;
        })
        .catch((err) => {
          if (seq !== this.loadSeq) return;
          this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) });
        })
        .finally(() => {
          // Only the current load may say loading has stopped: an older one finishing while the
          // newer is in flight would let the scroll observer start a third.
          if (seq !== this.loadSeq) return;
          this.loading = false;
          this.$nextTick(this.watchBottom);
        });
    },
    /**
     * Point the observer at the bottom of the list again.
     *
     * The marker only exists while there is more to fetch and once rows are on
     * screen, so it is re-found after every page. Observing it afresh also
     * reports its current state, so a page too short to scroll fetches the next
     * one straight away instead of waiting for a scroll that cannot happen.
     */
    watchBottom() {
      if (!this.observer) return;
      this.observer.disconnect();
      if (this.$refs.more) this.observer.observe(this.$refs.more);
    },
    /**
     * The accept queued a payments read of the account, so the policies just added get
     * their receipts without a second press of Read. Said here because nobody pressed
     * Read for it: it shows up under All updates like any other read.
     */
    sayPaymentsReadQueued() {
      this.$notify.success({
        message: this.$t('carriers.import.snapshot.PaymentsReadQueued', {
          carrier: this.carrierName || this.$t('carriers.import.TheCarrier'),
        }),
      });
    },
    /**
     * Apply or dismiss the selection, in chunks the server can actually finish.
     *
     * Sequential, not parallel: each row runs the real importer, and firing
     * sixteen of those at once is how you turn a slow screen into a slow
     * database. The APPLYING claim makes chunking safe on its own — a chunk
     * claims its own rows and a claim can only be taken once — so a chunk that
     * fails leaves its rows PENDING and the ones before it written.
     */
    resolve(what) {
      const ids = this.selected.slice();
      if (!ids.length) return Promise.resolve();
      this.busy = what;
      this.failures = {};
      this.notice = '';
      const action = what === 'apply'
        ? 'carrierImportApplySnapshot'
        : 'carrierImportDismissSnapshot';
      const chunks = [];
      const size = what === 'apply' ? APPLY_CHUNK : ids.length;
      for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));

      let done = 0;
      let failed = 0;
      const failures = {};
      // The payments reads the accept set going, by account: said once at the end,
      // because a person did not press Read for them and would otherwise wonder.
      const paymentsReads = {};
      this.progress = ids.length > size ? { done: 0, total: ids.length } : null;

      const step = (index) => {
        if (index >= chunks.length) return Promise.resolve();
        return this.$store.dispatch(action, chunks[index]).then((res) => {
          done += what === 'apply'
            ? ((res && res.appliedCount) || 0)
            : ((res && res.dismissedCount) || 0);
          failed += (res && res.failedCount) || 0;
          ((res && res.failures) || []).forEach((f) => {
            failures[f.id] = f.message || this.$t('adminTitles.someError');
          });
          ((res && res.paymentsReads) || []).forEach((r) => { paymentsReads[r.scrapeAccountId] = r; });
          if (this.progress) {
            this.progress = {
              done: Math.min(this.progress.done + chunks[index].length, ids.length),
              total: ids.length,
            };
          }
          return step(index + 1);
        });
      };

      return step(0)
        .then(() => {
          // A failed row stays PENDING and says why on itself; this line is the
          // batch's own answer, and it outlives a toast.
          this.notice = [
            this.$t(`carriers.import.snapshot.${what}Done`, { count: done }),
            failed ? this.$t('carriers.import.snapshot.applyFailed', { count: failed }) : '',
          ].filter(Boolean).join(' · ');
          this.clearSelection();
          if (Object.keys(paymentsReads).length) this.sayPaymentsReadQueued();
          this.$emit('resolved');
          return Promise.all([this.load(true), this.countTabs()])
            .then(() => { this.failures = failures; });
        })
        .catch((err) => this.$notify.error({
          message: apiError(err, this.$t('adminTitles.someError')),
        }))
        .finally(() => { this.busy = null; this.progress = null; });
    },
  },
};
</script>

<style lang="scss" scoped>
.snapshot-review {
  &__subtitle {
    font-size: 13px;
    font-weight: 400;
    color: var(--solid-dark-purple);
    margin-bottom: 12px;
  }

  &__empty {
    padding: 24px 0;
    font-size: 14px;
    color: var(--solid-dark-purple);
  }

  &__bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--solid-lavender-grey);
  }

  &__count {
    margin-left: auto;
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    font-variant-numeric: tabular-nums;
  }

  &__held {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
  }

  &__held-chip {
    padding: 1px 8px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 500;
    background: #fff2e5;
    color: #b8650f;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid var(--solid-lavender-grey);

    &:last-of-type { border-bottom: 0; }

    &--failed {
      background: var(--solid-red-012);
      border-radius: 8px;
      padding: 14px 12px;
    }
  }

  &__spacer { width: 14px; flex: none; }

  // BaseTabs floats; flow-root keeps it inside the drawer's flow.
  &__tabs {
    display: flow-root;
    margin-bottom: 14px;
  }

  &__side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    flex: none;
  }

  &__outcome {
    font-size: 12.5px;
    font-weight: 500;
    white-space: nowrap;

    &--waiting { color: var(--solid-dark-purple); }
    &--busy { color: var(--solid-orange); }
    &--skipped { color: var(--solid-black-purple-70); }

    &--done {
      color: var(--solid-green);
      text-decoration: none;

      &[href]:hover { text-decoration: underline; }
    }
  }

  &__main { flex: 1; min-width: 0; }

  &__policy {
    font-size: 14px;
    font-weight: 600;
    color: var(--solid-black-purple);
  }

  &__client-link {
    color: inherit;
    text-decoration: underline;
    text-decoration-color: var(--solid-lavender-grey);
    text-underline-offset: 2px;

    &:hover { color: var(--solid-purple); text-decoration-color: currentColor; }
  }

  &__doc {
    margin-left: 8px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--solid-purple);
    text-decoration: underline;
    text-underline-offset: 2px;
    vertical-align: middle;

    &:hover { text-decoration-color: currentColor; }
  }

  &__client {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 2px;
  }

  &__changes {
    width: 100%;
    margin-top: 8px;
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;

    th {
      text-align: left;
      font-weight: 500;
      color: var(--solid-dark-purple);
      padding: 0 12px 4px 0;
    }

    td {
      padding: 3px 12px 3px 0;
      vertical-align: top;
      // A list (services, beneficiaries) is one item per line, not one run-on line.
      white-space: pre-line;
    }
  }

  // The carrier's value is the one that would win, so it reads as the answer and
  // the current value reads as what it replaces.
  &__old {
    color: var(--solid-dark-purple);
    text-decoration: line-through;
  }

  &__new {
    color: var(--solid-black-purple);
    font-weight: 600;
  }

  &__badge {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 10px;
    background: #eef0ff;
    color: #4b4bd6;
    font-size: 12px;
    font-weight: 500;
    vertical-align: middle;
  }

  &__assign {
    margin-top: 8px;
  }

  &__same,
  &__why {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 6px;
  }

  // flex: none so a long client name does not squeeze it; the row aligns to
  // flex-start, so it sits level with the policy number.
  &__look {
    flex: none;
    margin-left: auto;
  }

  &__notice {
    margin-bottom: 12px;
    padding: 10px 14px;
    border-radius: 8px;
    background: var(--solid-light-purple);
    color: var(--solid-black-purple);
    font-size: 13px;
    line-height: 1.5;
  }

  &__failure {
    font-size: 12.5px;
    color: var(--solid-red);
    margin-top: 6px;
  }

  &__progress {
    margin-right: auto;
    min-width: 220px;
    max-width: 320px;
  }

  &__progress-text {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    font-variant-numeric: tabular-nums;
  }

  &__progress-track {
    height: 4px;
    border-radius: 2px;
    background: var(--solid-lavender-grey);
    margin-top: 6px;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    border-radius: 2px;
    background: var(--solid-purple);
    transition: width 0.25s ease;
  }

  // Tall enough to be seen by the observer before the spinner is in it.
  &__more {
    display: flex;
    justify-content: center;
    min-height: 32px;
    margin-top: 12px;
  }

  &__note {
    margin-right: auto;
    font-size: 12.5px;
    line-height: 1.4;
    color: var(--solid-dark-purple);
  }
}
</style>
